/**
 * Phase 5 — Advanced JSON-LD Schema Factory
 *
 * Reusable, LLM/rich-result-optimized schema builders for programmatic pages.
 * Adds: OfferCatalog, DefinedTermSet, ImageObject, VideoObject,
 * SpeakableSpecification, ItemList, and Service+Offer enhancements.
 *
 * Design rules:
 *  - All schemas MUST include `@context` = https://schema.org
 *  - Deterministic `@id` per node to enable entity graph (@graph) links
 *  - No external HTTP calls — pure factories, safe for SSR/synchronous injection
 */

const ORIGIN = 'https://zarooriretail.com';
const ORG_ID = `${ORIGIN}/#organization`;
const AUTHOR_ID = `${ORIGIN}/deepinder-ahluwalia#person`;

// ============================================================================
// OFFER CATALOG — for pages that list services or ranked alternatives
// ============================================================================
export const buildOfferCatalogSchema = (name, items = [], pageUrl) => {
  if (!items.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'OfferCatalog',
    '@id': `${pageUrl}#offer-catalog`,
    name,
    itemListElement: items.map((item, i) => ({
      '@type': 'Offer',
      position: i + 1,
      itemOffered: {
        '@type': item.type || 'Service',
        name: item.name,
        description: item.description || item.blurb || '',
        ...(item.url && { url: item.url.startsWith('http') ? item.url : `${ORIGIN}${item.url}` }),
      },
      ...(item.price && {
        price: item.price,
        priceCurrency: item.currency || 'INR',
      }),
    })),
  };
};

// ============================================================================
// DEFINED TERM SET — glossary of marketplace/industry terms for entity linking
// ============================================================================
export const buildDefinedTermSetSchema = (name, terms = [], pageUrl) => {
  if (!terms.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    '@id': `${pageUrl}#glossary`,
    name,
    hasDefinedTerm: terms.map((t, i) => ({
      '@type': 'DefinedTerm',
      '@id': `${pageUrl}#term-${(t.termCode || t.name || i).toString().toLowerCase().replace(/\s+/g, '-')}`,
      name: t.name,
      description: t.description,
      ...(t.url && { url: t.url.startsWith('http') ? t.url : `${ORIGIN}${t.url}` }),
      ...(t.termCode && { termCode: t.termCode }),
      inDefinedTermSet: { '@id': `${pageUrl}#glossary` },
    })),
  };
};

// ============================================================================
// IMAGE OBJECT — enriches Article/WebPage with primary image
// ============================================================================
export const buildImageObjectSchema = (url, { caption, name, width, height } = {}) => {
  if (!url) return null;
  const fullUrl = url.startsWith('http') ? url : `${ORIGIN}${url}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    '@id': `${fullUrl}#image`,
    url: fullUrl,
    contentUrl: fullUrl,
    ...(name && { name }),
    ...(caption && { caption }),
    ...(width && { width }),
    ...(height && { height }),
    creditText: 'Zaroori Retail',
    creator: { '@id': ORG_ID },
    copyrightNotice: '© Zaroori Retail',
    license: `${ORIGIN}/terms`,
  };
};

// ============================================================================
// VIDEO OBJECT — optional, for pages with video embeds
// ============================================================================
export const buildVideoObjectSchema = ({ url, thumbnailUrl, name, description, uploadDate, duration }) => {
  if (!url || !name) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name,
    description: description || name,
    thumbnailUrl: thumbnailUrl || `${ORIGIN}/og-image.png`,
    uploadDate: uploadDate || '2026-02-01',
    contentUrl: url,
    embedUrl: url,
    ...(duration && { duration }),
    publisher: { '@id': ORG_ID },
  };
};

// ============================================================================
// SPEAKABLE SPECIFICATION — voice/AI-first content extraction hints
// ============================================================================
export const buildSpeakableSchema = (pageUrl, cssSelectors = ['[data-speakable="true"]', 'h1', 'h2']) => ({
  '@context': 'https://schema.org',
  '@type': 'SpeakableSpecification',
  '@id': `${pageUrl}#speakable`,
  cssSelector: cssSelectors,
});

// ============================================================================
// ITEM LIST — ranked alternatives, related pages, series
// ============================================================================
export const buildItemListSchema = (name, items = [], pageUrl) => {
  if (!items.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${pageUrl}#itemlist`,
    name,
    numberOfItems: items.length,
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      ...(it.url && { url: it.url.startsWith('http') ? it.url : `${ORIGIN}${it.url}` }),
      ...(it.description && { description: it.description }),
    })),
  };
};

// ============================================================================
// SERVICE + AGGREGATE OFFER — combined for consultant/agency pages
// ============================================================================
export const buildServiceWithOffersSchema = ({
  pageUrl, name, description, serviceType, areaServed = 'India',
  offers = [],
  minPrice, maxPrice, currency = 'INR',
}) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${pageUrl}#service`,
    name,
    description,
    serviceType,
    provider: { '@id': ORG_ID },
    areaServed: { '@type': 'Country', name: areaServed },
  };
  if (offers.length) {
    schema.hasOfferCatalog = {
      '@type': 'OfferCatalog',
      name: `${name} — Offerings`,
      itemListElement: offers.map((o, i) => ({
        '@type': 'Offer',
        position: i + 1,
        name: o.name,
        description: o.description || '',
        ...(o.price && { price: o.price, priceCurrency: currency }),
      })),
    };
  }
  if (minPrice && maxPrice) {
    schema.offers = {
      '@type': 'AggregateOffer',
      lowPrice: minPrice,
      highPrice: maxPrice,
      priceCurrency: currency,
      offerCount: offers.length || 1,
      availability: 'https://schema.org/InStock',
    };
  }
  return schema;
};

// ============================================================================
// MARKETPLACE GLOSSARY — canonical DefinedTerm entries used across pages
// (Phase 6 will link these into a Knowledge Graph)
// ============================================================================
export const MARKETPLACE_GLOSSARY = {
  myntra: { name: 'Myntra', description: 'India\'s largest fashion marketplace by Flipkart Group, focused on branded apparel, footwear, beauty, and accessories.', url: '/myntra-account-management', termCode: 'MYNTRA' },
  nykaa: { name: 'Nykaa', description: 'Leading beauty and personal-care marketplace in India, split into Nykaa (Beauty) and Nykaa Fashion verticals.', url: '/nykaa-account-management', termCode: 'NYKAA' },
  'nykaa-fashion': { name: 'Nykaa Fashion', description: 'Fashion vertical of Nykaa Group, covering branded apparel, footwear, and accessories with a premium/curated buyer profile.', url: '/nykaa-fashion-account-management', termCode: 'NYKAA_FASHION' },
  ajio: { name: 'AJIO', description: 'Reliance Retail-owned fashion marketplace with strong AJIO Luxe curated luxury segment.', url: '/ajio-account-management', termCode: 'AJIO' },
  'tata-cliq': { name: 'Tata CLiQ', description: 'Tata Digital marketplace covering fashion, electronics, and luxury (Tata CLiQ Luxury) with trust-first brand curation.', url: '/marketplaces', termCode: 'TATA_CLIQ' },
  tira: { name: 'Tira', description: 'Reliance Retail\'s premium beauty marketplace with luxury-first curation for global and Indian brands.', url: '/tira-brand-approval', termCode: 'TIRA' },
  'amazon-fashion': { name: 'Amazon Fashion India', description: 'Amazon India\'s Fashion vertical (formerly Amazon Fashion), including Amazon Luxury Stores.', url: '/marketplaces', termCode: 'AMAZON_FASHION' },
  blinkit: { name: 'Blinkit', description: 'Zomato-owned quick commerce platform (formerly Grofers) with 10-minute grocery + FMCG delivery.', url: '/blinkit-account-management', termCode: 'BLINKIT' },
  zepto: { name: 'Zepto', description: 'Independent quick commerce platform with 10-minute grocery + FMCG + personal-care delivery.', url: '/zepto-account-management', termCode: 'ZEPTO' },
  'swiggy-instamart': { name: 'Swiggy Instamart', description: 'Swiggy\'s quick commerce vertical with 10-15 minute grocery + FMCG delivery.', url: '/swiggy-instamart-account-management', termCode: 'SWIGGY_INSTAMART' },
  firstcry: { name: 'FirstCry', description: 'India\'s largest baby, kids, and maternity marketplace, majority-owned by SoftBank.', url: '/firstcry-seller-registration', termCode: 'FIRSTCRY' },
  bigbasket: { name: 'BigBasket', description: 'Tata-owned grocery + FMCG marketplace with both slotted and BB Now (quick-commerce) delivery.', url: '/bigbasket-seller-registration', termCode: 'BIGBASKET' },
  noon: { name: 'Noon', description: 'Leading MENA marketplace headquartered in UAE, covering fashion, beauty, electronics, and grocery.', url: '/noon-seller-registration-uae', termCode: 'NOON' },
  namshi: { name: 'Namshi', description: 'UAE-based fashion marketplace acquired by Noon Group, focused on branded fashion + footwear + beauty.', url: '/namshi-seller-registration-uae', termCode: 'NAMSHI' },
  ounass: { name: 'Ounass', description: 'Al Tayer Group\'s luxury fashion marketplace in UAE, focused on premium and luxury brands.', url: '/ounass-seller-registration-uae', termCode: 'OUNASS' },
  meesho: { name: 'Meesho', description: 'Value-first Indian marketplace with strong Tier-2/3 city penetration and mostly unbranded/private-label focus.', url: '/marketplaces', termCode: 'MEESHO' },
  flipkart: { name: 'Flipkart', description: 'Walmart-owned Indian marketplace, largest by GMV; parent of Myntra and Shopsy.', url: '/marketplaces', termCode: 'FLIPKART' },
  amazon: { name: 'Amazon India', description: 'Amazon\'s India marketplace covering fashion, electronics, grocery, and premium categories including Amazon Luxury Stores.', url: '/marketplaces', termCode: 'AMAZON' },
  'pernia-pop-up-shop': { name: 'Pernia Pop-Up Shop', description: 'Premium Indian designer marketplace curated by Pernia Qureshi — ethnic, occasion & luxury designer wear.', url: '/marketplaces', termCode: 'PERNIA' },
  'aza-fashions': { name: 'Aza Fashions', description: 'Indian luxury & designer marketplace focused on couture, ethnic occasion wear, and premium jewellery.', url: '/marketplaces', termCode: 'AZA' },
  purplle: { name: 'Purplle', description: 'Indian beauty & personal-care marketplace focused on masstige and value-tier D2C brands.', url: '/marketplaces', termCode: 'PURPLLE' },
  mirraw: { name: 'Mirraw', description: 'Indian ethnic-wear + jewellery marketplace with global export presence and value-tier positioning.', url: '/marketplaces', termCode: 'MIRRAW' },
  jaypore: { name: 'Jaypore', description: 'ABFRL-owned artisanal Indian craft marketplace focused on handloom, jewellery, and home décor.', url: '/marketplaces', termCode: 'JAYPORE' },
};

// ============================================================================
// INDUSTRY GLOSSARY — canonical DefinedTerm entries for verticals
// ============================================================================
export const INDUSTRY_GLOSSARY = {
  fashion: { name: 'Fashion (Apparel)', description: 'Branded and D2C apparel — men\'s, women\'s, and kids\' clothing across price bands.', termCode: 'FASHION' },
  beauty: { name: 'Beauty', description: 'Skincare, cosmetics, personal-grooming and premium/luxury beauty categories.', termCode: 'BEAUTY' },
  cosmetics: { name: 'Cosmetics', description: 'Color cosmetics (lipstick, foundation, eye) and makeup brands.', termCode: 'COSMETICS' },
  'personal-care': { name: 'Personal Care', description: 'Haircare, bath & body, wellness, hygiene, and daily-use personal-care brands.', termCode: 'PERSONAL_CARE' },
  jewellery: { name: 'Jewellery', description: 'Fine jewellery (gold/diamond) and fashion/artificial jewellery.', termCode: 'JEWELLERY' },
  footwear: { name: 'Footwear', description: 'Formal, casual, sports, and luxury footwear across categories.', termCode: 'FOOTWEAR' },
  'home-living': { name: 'Home & Living', description: 'Home decor, kitchenware, bedding, and lifestyle products.', termCode: 'HOME_LIVING' },
  kidswear: { name: 'Kidswear', description: 'Kids\' apparel, footwear, accessories, and infant essentials (0-14 years).', termCode: 'KIDSWEAR' },
  fmcg: { name: 'FMCG', description: 'Fast-moving consumer goods — packaged foods, beverages, staples, snacks, and daily essentials.', termCode: 'FMCG' },
  'health-wellness': { name: 'Health & Wellness', description: 'Nutraceuticals, sports nutrition, wellness supplements, health snacks.', termCode: 'HEALTH_WELLNESS' },
};

// ============================================================================
// HELPERS: pick glossary terms for a given page
// ============================================================================
export const glossaryTermsForMarketplaces = (slugs = []) =>
  slugs.map((s) => MARKETPLACE_GLOSSARY[s]).filter(Boolean);

export const glossaryTermsForIndustries = (slugs = []) =>
  slugs.map((s) => INDUSTRY_GLOSSARY[s]).filter(Boolean);

export { ORIGIN, ORG_ID, AUTHOR_ID };

// ============================================================================
// PHASE 6 + 9 — ENTITY GRAPH BUILDER
// One @graph node connecting Founder ↔ Organization ↔ Services ↔ Marketplaces
// ↔ DefinedTerms. Injects the "canonical entity fingerprint" onto every page
// so LLMs can resolve Zaroori Retail's knowledge graph without external calls.
// ============================================================================
const FOUNDER_IMAGE = 'https://customer-assets.emergentagent.com/job_marketplace-magic-3/artifacts/x83k0yhy_photo_2025-07-15_15-19-41.jpg';

export const buildEntityGraph = ({
  pageUrl,
  pageTitle,
  pageDescription,
  primaryMarketplaceSlugs = [],
  primaryIndustrySlugs = [],
  primaryServices = [],
  pageType = 'WebPage', // WebPage | Article | CollectionPage | FAQPage
} = {}) => {
  // ============== CORE ORGANIZATION ==============
  const organization = {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: 'Zaroori Retail',
    alternateName: ['Zaroori Retail Consulting', 'Zaroori Retail — Marketplace Onboarding'],
    url: ORIGIN,
    logo: {
      '@type': 'ImageObject',
      '@id': `${ORIGIN}/#logo`,
      url: `${ORIGIN}/logo.png`,
      contentUrl: `${ORIGIN}/logo.png`,
      width: 512,
      height: 512,
      caption: 'Zaroori Retail Logo',
    },
    foundingDate: '2016-06-01',
    founder: { '@id': AUTHOR_ID },
    numberOfEmployees: { '@type': 'QuantitativeValue', value: 120 },
    sameAs: [
      'https://www.linkedin.com/company/zaroori-retail',
      'https://www.instagram.com/zarooriretail',
      'https://twitter.com/zarooriretail',
      'https://www.facebook.com/zarooriretail',
      'https://www.youtube.com/@zarooriretail',
    ],
    address: {
      '@type': 'PostalAddress',
      '@id': `${ORIGIN}/#address`,
      streetAddress: 'Sector 63',
      addressLocality: 'Noida',
      addressRegion: 'Delhi NCR',
      postalCode: '201301',
      addressCountry: 'IN',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        '@id': `${ORIGIN}/#contact-sales`,
        telephone: '+91-77430-05242',
        contactType: 'sales',
        areaServed: ['IN', 'AE', 'US'],
        availableLanguage: ['English', 'Hindi'],
      },
      {
        '@type': 'ContactPoint',
        '@id': `${ORIGIN}/#contact-support`,
        email: 'deepinder@zarooriretail.com',
        contactType: 'customer support',
        areaServed: 'Worldwide',
        availableLanguage: ['English', 'Hindi'],
      },
    ],
    areaServed: [
      { '@type': 'Country', '@id': `${ORIGIN}/#country-in`, name: 'India' },
      { '@type': 'Country', '@id': `${ORIGIN}/#country-ae`, name: 'United Arab Emirates' },
      { '@type': 'Country', '@id': `${ORIGIN}/#country-us`, name: 'United States' },
    ],
    knowsAbout: [
      'Marketplace Onboarding', 'Brand Approval', 'Marketplace Consulting',
      'Order Management System (OMS)', 'Marketplace Advertising',
      'Quick Commerce', 'D2C Growth', 'Marketplace Reconciliation',
      ...primaryMarketplaceSlugs.map((s) => MARKETPLACE_GLOSSARY[s]?.name).filter(Boolean),
      ...primaryIndustrySlugs.map((s) => INDUSTRY_GLOSSARY[s]?.name).filter(Boolean),
    ],
    aggregateRating: {
      '@type': 'AggregateRating', '@id': `${ORG_ID}#rating`,
      ratingValue: '4.9', reviewCount: '200', bestRating: '5', worstRating: '1',
    },
    audience: [
      { '@type': 'Audience', '@id': `${ORIGIN}/#audience-d2c`, audienceType: 'D2C Brands', geographicArea: 'India' },
      { '@type': 'Audience', '@id': `${ORIGIN}/#audience-enterprise`, audienceType: 'Enterprise Retailers', geographicArea: 'India + Middle East' },
      { '@type': 'Audience', '@id': `${ORIGIN}/#audience-sme`, audienceType: 'SME Brands', geographicArea: 'India' },
    ],
  };

  // ============== FOUNDER PERSON ==============
  const founder = {
    '@type': 'Person',
    '@id': AUTHOR_ID,
    name: 'Deepinder Ahluwalia',
    givenName: 'Deepinder',
    familyName: 'Ahluwalia',
    jobTitle: 'Founder & CEO',
    worksFor: { '@id': ORG_ID },
    url: `${ORIGIN}/deepinder-ahluwalia`,
    image: FOUNDER_IMAGE,
    alumniOf: [
      { '@type': 'CollegeOrUniversity', '@id': `${ORIGIN}/#iim`, name: 'IIM (Indian Institute of Management)' },
    ],
    knowsAbout: [
      'Marketplace Onboarding', 'Myntra', 'Nykaa', 'AJIO', 'Tata CLiQ', 'Amazon India',
      'Blinkit', 'Zepto', 'Swiggy Instamart', 'Quick Commerce', 'D2C Growth Strategy',
      'Brand Approval Process', 'Category Buyer Relationships',
      'Order Management Systems', 'AI Repricing', 'PLA Bid Management',
    ],
    sameAs: ['https://www.linkedin.com/in/deepinderahluwalia'],
    description: 'Founder & CEO of Zaroori Retail. IIM alum, ex-Reliance Retail. Onboarded 4,000+ brands across 30+ marketplaces since 2016.',
  };

  // ============== ZAROORI JINI OMS SOFTWAREAPPLICATION ==============
  // Phase X.6.1 (Feb 24 2026) — per user directive:
  //   • Removed fabricated aggregateRating (no genuine reviews yet)
  //   • Removed hardcoded numeric prices (pricing not finalised)
  //   • Added required `image` property
  //   • Migrated @id + url from /oms → /zaroori-jini-oms
  const zjOms = {
    '@type': 'SoftwareApplication',
    '@id': `${ORIGIN}/zaroori-jini-oms#software`,
    name: 'Zaroori Jini OMS',
    alternateName: 'ZJ OMS',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Order Management System (OMS)',
    operatingSystem: 'Web (all modern browsers)',
    description: "India's founder-led, marketplace-native AI OMS with 30+ marketplace connectors and 5 pre-built AI capabilities.",
    url: `${ORIGIN}/zaroori-jini-oms`,
    image: `${ORIGIN}/og-image.png`,
    brand: { '@type': 'Brand', name: 'Zaroori Jini OMS' },
    provider: { '@id': ORG_ID },
    creator: { '@id': ORG_ID },
    featureList: [
      'Real-time order webhook', '15-min inventory sync', 'AI Repricing',
      'PLA Bid Manager', 'Weekly auto-reconciliation', 'WMS Lite',
      'ERP integrations', '30+ marketplace connectors',
    ].join(' | '),
    // Offers, aggregateRating, and review deliberately omitted until real values exist.
  };

  // ============== SERVICES ==============
  const services = primaryServices.length ? primaryServices : [
    { name: 'Marketplace Registration', slug: 'marketplace-registration' },
    { name: 'Brand Approval', slug: 'brand-approval' },
    { name: 'Marketplace Consulting', slug: 'marketplace-consultant' },
    { name: 'Marketplace Advertising & PLA', slug: 'marketplace-advertising' },
    { name: 'Marketplace Account Management', slug: 'marketplace-account-management' },
  ];
  const serviceNodes = services.map((s) => ({
    '@type': 'Service',
    '@id': `${ORIGIN}/${s.slug}#service`,
    name: s.name,
    provider: { '@id': ORG_ID },
    areaServed: { '@type': 'Country', name: 'India' },
    serviceType: s.name,
  }));

  // ============== MARKETPLACE + INDUSTRY ENTITIES ==============
  const marketplaceEntities = primaryMarketplaceSlugs.map((slug) => {
    const m = MARKETPLACE_GLOSSARY[slug];
    if (!m) return null;
    return {
      '@type': 'Brand',
      '@id': `${ORIGIN}/#brand-${slug}`,
      name: m.name,
      description: m.description,
      url: m.url ? `${ORIGIN}${m.url}` : undefined,
    };
  }).filter(Boolean);

  const industryEntities = primaryIndustrySlugs.map((slug) => {
    const ind = INDUSTRY_GLOSSARY[slug];
    if (!ind) return null;
    return {
      '@type': 'Thing',
      '@id': `${ORIGIN}/#industry-${slug}`,
      name: ind.name,
      description: ind.description,
    };
  }).filter(Boolean);

  // ============== WEBPAGE / ARTICLE / COLLECTION / FAQ ==============
  const webpage = pageUrl ? {
    '@type': pageType,
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    name: pageTitle,
    description: pageDescription,
    isPartOf: { '@id': `${ORIGIN}/#website` },
    about: [
      ...marketplaceEntities.map((m) => ({ '@id': m['@id'] })),
      ...industryEntities.map((i) => ({ '@id': i['@id'] })),
    ],
    mentions: [
      ...serviceNodes.map((s) => ({ '@id': s['@id'] })),
      { '@id': `${ORIGIN}/zaroori-jini-oms#software` },
    ],
    primaryImageOfPage: { '@id': `${ORIGIN}/#logo` },
    speakable: { '@id': `${pageUrl}#speakable` },
  } : null;

  // ============== WEBSITE ==============
  const website = {
    '@type': 'WebSite',
    '@id': `${ORIGIN}/#website`,
    url: ORIGIN,
    name: 'Zaroori Retail',
    alternateName: 'Zaroori Retail — Marketplace Authority',
    publisher: { '@id': ORG_ID },
    inLanguage: 'en-IN',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${ORIGIN}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  // ============== SITE NAVIGATION ELEMENTS ==============
  const siteNav = {
    '@type': 'SiteNavigationElement',
    '@id': `${ORIGIN}/#nav`,
    name: 'Zaroori Retail Navigation',
    hasPart: [
      { '@type': 'SiteNavigationElement', name: 'Home', url: ORIGIN },
      { '@type': 'SiteNavigationElement', name: 'Services', url: `${ORIGIN}/services` },
      { '@type': 'SiteNavigationElement', name: 'Zaroori Jini OMS', url: `${ORIGIN}/zaroori-jini-oms` },
      { '@type': 'SiteNavigationElement', name: 'Marketplaces', url: `${ORIGIN}/marketplaces` },
      { '@type': 'SiteNavigationElement', name: 'Blog', url: `${ORIGIN}/blog` },
      { '@type': 'SiteNavigationElement', name: 'Contact', url: `${ORIGIN}/contact` },
    ],
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      organization,
      founder,
      zjOms,
      website,
      siteNav,
      ...(webpage ? [webpage] : []),
      ...serviceNodes,
      ...marketplaceEntities,
      ...industryEntities,
    ],
  };
};

// ============================================================================
// PHASE 7 — Author Authority + Phase 11 — Reviewed / Fact-Checked metadata
// ============================================================================
export const AUTHOR_AUTHORITY = {
  person: {
    '@type': 'Person',
    '@id': AUTHOR_ID,
    name: 'Deepinder Ahluwalia',
    jobTitle: 'Founder & CEO, Zaroori Retail',
    url: `${ORIGIN}/deepinder-ahluwalia`,
    image: FOUNDER_IMAGE,
    sameAs: ['https://www.linkedin.com/in/deepinderahluwalia'],
    knowsAbout: [
      'Marketplace Onboarding', 'Brand Approval', 'PLA Advertising',
      'D2C Growth', 'Order Management', 'Marketplace Reconciliation',
    ],
  },
  reviewedBy: {
    '@type': 'Person',
    '@id': AUTHOR_ID,
    name: 'Deepinder Ahluwalia',
    jobTitle: 'Founder & CEO, Zaroori Retail',
  },
};

// Add these to any Article schema for Phase 11 fact-check signals
export const factCheckAnnotations = ({ dateModified = '2026-02-22', datePublished = '2026-02-15' } = {}) => ({
  datePublished,
  dateModified,
  reviewedBy: AUTHOR_AUTHORITY.reviewedBy,
  editor: AUTHOR_AUTHORITY.reviewedBy,
});


// ============================================================================
// FEB 2026 — Entity Authority Schema Builders (Phase 6 expansion)
//   Award · ProfessionalService · LocalBusiness · HowTo · VideoObject
//   Review · Speakable · Person (Founder) · Organization (canonical)
// All builders produce a *node* suitable for placement inside a @graph.
// ============================================================================

/** Canonical Organization node (Feb 2026) — reads from ORGANIZATION data. */
export const buildCanonicalOrganizationNode = (org, offices = []) => ({
  '@type': org['@type'] || 'Organization',
  '@id': org['@id'] || ORG_ID,
  name: org.name,
  alternateName: org.alternateName,
  legalName: org.legalName,
  url: org.url,
  logo: org.logo,
  image: org.image,
  description: org.description,
  slogan: org.slogan,
  foundingDate: org.foundingDate,
  founder: org.founder,
  numberOfEmployees: org.numberOfEmployees,
  areaServed: org.areaServed,
  knowsAbout: org.knowsAbout,
  award: org.award,
  sameAs: org.sameAs,
  contactPoint: org.contactPoint,
  address: offices,
});

/** Founder / Person node (Feb 2026). */
export const buildFounderNode = (founder) => ({
  '@type': 'Person',
  '@id': founder['@id'],
  name: founder.name,
  givenName: founder.givenName,
  familyName: founder.familyName,
  jobTitle: founder.jobTitle,
  worksFor: founder.worksFor,
  alumniOf: founder.alumniOf,
  knowsAbout: founder.knowsAbout,
  url: founder.url,
  image: founder.image,
  sameAs: founder.sameAs,
  description: founder.description,
  award: founder.award,
});

/** ProfessionalService node — commercial-intent variant of Organization. */
export const buildProfessionalServiceNode = ({
  orgId = ORG_ID, name, description, url, priceRange = '₹75,000-2,50,000',
  areaServed = 'India', services = [],
} = {}) => ({
  '@type': 'ProfessionalService',
  '@id': `${url}#professional-service`,
  name,
  description,
  url,
  parentOrganization: { '@id': orgId },
  priceRange,
  areaServed: { '@type': 'Country', name: areaServed },
  hasOfferCatalog: services.length
    ? {
        '@type': 'OfferCatalog',
        name: `${name} — Service Catalog`,
        itemListElement: services.map((s, i) => ({
          '@type': 'Offer',
          position: i + 1,
          itemOffered: {
            '@type': 'Service',
            name: s.name,
            description: s.description,
            url: s.url,
            ...(s.priceRange && { offers: { '@type': 'Offer', priceRange: s.priceRange } }),
          },
        })),
      }
    : undefined,
});

/** LocalBusiness node (per office). Emits an array. */
export const buildLocalBusinessNodes = (orgName, offices = []) =>
  offices.map((o) => ({
    '@type': 'LocalBusiness',
    '@id': `${ORIGIN}/#office-${(o.name || o.addressLocality || 'hq').toLowerCase().replace(/\s+/g, '-')}`,
    name: `${orgName} — ${o.name || o.addressLocality}`,
    address: o,
    telephone: o.telephone,
    parentOrganization: { '@id': ORG_ID },
  }));

/** HowTo node — for step-by-step approval / onboarding pages. */
export const buildHowToNode = ({ pageUrl, name, description, totalTimeISO = 'PT15D', supply = [], tool = [], steps = [] }) => ({
  '@type': 'HowTo',
  '@id': `${pageUrl}#howto`,
  name,
  description,
  totalTime: totalTimeISO,
  supply: supply.map((s) => ({ '@type': 'HowToSupply', name: s })),
  tool: tool.map((t) => ({ '@type': 'HowToTool', name: t })),
  step: steps.map((s, i) => ({
    '@type': 'HowToStep',
    position: i + 1,
    name: s.name || s.title,
    text: s.text || s.description,
    ...(s.url && { url: s.url }),
    ...(s.image && { image: s.image }),
  })),
});

/** VideoObject node — for embed sections on AI-answer pages. */
export const buildVideoObjectNode = ({ pageUrl, name, description, thumbnailUrl, uploadDate = '2026-02-22', durationISO = 'PT3M', embedUrl }) => ({
  '@type': 'VideoObject',
  '@id': `${pageUrl}#video`,
  name,
  description,
  thumbnailUrl,
  uploadDate,
  duration: durationISO,
  ...(embedUrl && { embedUrl, contentUrl: embedUrl }),
  publisher: { '@id': ORG_ID },
});

/** Review node — reusable for case studies + landing pages. */
export const buildReviewNode = ({ pageUrl, ratingValue = 5, author, reviewBody, datePublished = '2026-02-22', itemReviewed }) => ({
  '@type': 'Review',
  '@id': `${pageUrl}#review-${(author || 'anon').toLowerCase().replace(/\s+/g, '-')}`,
  reviewRating: { '@type': 'Rating', ratingValue, bestRating: 5, worstRating: 1 },
  author: { '@type': 'Person', name: author || 'Verified Client' },
  reviewBody,
  datePublished,
  itemReviewed: itemReviewed || { '@id': ORG_ID },
});

/** AggregateRating derived from a list of case-study ratings. */
export const buildAggregateRatingNode = ({ pageUrl, ratings = [], itemReviewed }) => {
  if (!ratings.length) return null;
  const ratingValue = Math.round((ratings.reduce((a, r) => a + (r || 0), 0) / ratings.length) * 10) / 10;
  return {
    '@type': 'AggregateRating',
    '@id': `${pageUrl}#aggregate-rating`,
    ratingValue,
    ratingCount: ratings.length,
    bestRating: 5,
    worstRating: 1,
    itemReviewed: itemReviewed || { '@id': ORG_ID },
  };
};

/** Speakable specification — flags AI-summary blocks for voice assistants. */
export const buildSpeakableNode = ({ pageUrl, cssSelectors = ['[data-speakable="true"]', '.ai-summary-answer', '.quick-answer'] }) => ({
  '@type': 'SpeakableSpecification',
  '@id': `${pageUrl}#speakable`,
  cssSelector: cssSelectors,
});

/** Award node — enumerates every org award for the entity hub. */
export const buildAwardNodes = (orgAwards = []) =>
  orgAwards.map((a, i) => ({
    '@type': 'Award',
    '@id': `${ORIGIN}/#award-${i + 1}`,
    name: a,
    recipient: { '@id': ORG_ID },
  }));

/** Master Entity Authority Graph — combines everything into a single @graph.
 *  Used by the Entity Hub page + top-of-funnel AI-answer pages. */
export const buildEntityAuthorityGraph = ({
  pageUrl, pageTitle, pageDescription,
  organization, founder, offices = [],
  services = [], addSpeakable = true, addAwards = true,
  faqs = [], breadcrumbs = [],
  extraNodes = [],
} = {}) => {
  const orgNode = buildCanonicalOrganizationNode(organization, offices);
  const founderNode = founder ? buildFounderNode(founder) : null;

  const webPageNode = {
    '@type': 'WebPage',
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    name: pageTitle,
    description: pageDescription,
    isPartOf: { '@id': `${ORIGIN}/#website` },
    about: { '@id': ORG_ID },
    inLanguage: 'en-IN',
    dateModified: '2026-02-22',
    ...(addSpeakable && { speakable: { '@id': `${pageUrl}#speakable` } }),
  };

  const proServiceNode = services.length
    ? buildProfessionalServiceNode({
        name: organization.name,
        description: organization.description,
        url: pageUrl,
        services: services.slice(0, 15),
      })
    : null;

  const localBiz = buildLocalBusinessNodes(organization.name, offices);
  const awardNodes = addAwards ? buildAwardNodes(organization.award || []) : [];
  const speakableNode = addSpeakable ? buildSpeakableNode({ pageUrl }) : null;

  const breadcrumbList = breadcrumbs.length
    ? {
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumb`,
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem', position: i + 1, name: b.name, item: b.url,
        })),
      }
    : null;

  const faqNode = faqs.length
    ? {
        '@type': 'FAQPage',
        '@id': `${pageUrl}#faq`,
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  const website = {
    '@type': 'WebSite',
    '@id': `${ORIGIN}/#website`,
    url: ORIGIN,
    name: 'Zaroori Retail',
    publisher: { '@id': ORG_ID },
    inLanguage: 'en-IN',
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${ORIGIN}/search?q={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      orgNode,
      website,
      webPageNode,
      founderNode,
      proServiceNode,
      ...localBiz,
      ...awardNodes,
      speakableNode,
      breadcrumbList,
      faqNode,
      ...extraNodes,
    ].filter(Boolean),
  };
};

